import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// Save-and-restore window scroll position per pathname across in-app
// navigations. Uses sessionStorage keyed by pathname + optional key so nested
// routes (like different character detail pages) don't collide.
//
// Behavior:
//   - On any scroll (throttled with rAF), record window.scrollY under the
//     current pathname.
//   - On location change:
//       * If the user did a POP navigation (browser back/forward) AND we have
//         a saved offset for that path, restore it after the DOM paints.
//       * Otherwise (PUSH/REPLACE = forward-nav to a new page), scroll to top.
//
// This should be mounted once, high in the tree (App.js).
const STORAGE_KEY = "ica_scroll_positions_v1";

function readAll() {
  try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {}; }
  catch { return {}; }
}
function writeAll(obj) {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(obj)); } catch {}
}

export default function useScrollRestoration() {
  const location = useLocation();
  const navType = useNavigationType(); // "POP" | "PUSH" | "REPLACE"
  const key = location.pathname + location.search;

  // Continuously record scroll for the current path.
  useEffect(() => {
    let raf = null;
    const record = () => {
      raf = null;
      const all = readAll();
      all[key] = window.scrollY;
      writeAll(all);
    };
    const onScroll = () => {
      if (raf != null) return;
      raf = requestAnimationFrame(record);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf != null) cancelAnimationFrame(raf);
      // NOTE: We deliberately do NOT snapshot window.scrollY here.
      // By the time this cleanup runs, React has committed the next route's
      // DOM which may be shorter than the previous page — at which point the
      // browser has already clamped window.scrollY down (often to 0). Writing
      // that clamped value would clobber the correct position captured by the
      // rAF-throttled `onScroll` listener above.
    };
  }, [key]);

  // Restore (or reset) on route change.
  useEffect(() => {
    if (navType === "POP") {
      const all = readAll();
      const saved = all[key];
      const target = typeof saved === "number" ? saved : 0;
      if (target === 0) {
        window.scrollTo(0, 0);
        return;
      }
      // The new page's list may render asynchronously (fetch → render).
      // Poll for up to ~2s, scrolling to target once the document is tall
      // enough. Bail early once we successfully reach the target.
      let cancelled = false;
      let elapsed = 0;
      const step = () => {
        if (cancelled) return;
        window.scrollTo(0, target);
        // If the browser clamped us short (content not tall enough yet), retry.
        if (Math.abs(window.scrollY - target) < 2 || elapsed >= 2000) return;
        elapsed += 80;
        setTimeout(step, 80);
      };
      // Kick off after the first paint so React commits before we scroll.
      const raf = requestAnimationFrame(() => requestAnimationFrame(step));
      return () => {
        cancelled = true;
        cancelAnimationFrame(raf);
      };
    }
    // Fresh navigation: start at top.
    window.scrollTo(0, 0);
  }, [key, navType]);
}
